import React, { useMemo, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import { useTable, useFilters, useBlockLayout } from "react-table";
import scrollbarWidth from "../TableHelperFunctions/scrollbarWidth";
import { WarningMessage } from "./WarningMessage";
import styled from "styled-components";
import "react-datepicker/dist/react-datepicker.css";

const TopPanelContainer = styled.div`
  background: #e0e0e0;
  color: var(--clr-grey-1);
  border-bottom: 1px solid #e5e5e5;
  max-width: 100%;
  overflow-x: auto;
  padding: 10px 12px;
`;

const FormFieldsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
`;

const FormField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85em;

  label {
    white-space: nowrap;
    font-weight: 700;
  }

  input,
  select {
    font-size: 1.05em;
    padding: 8px 6px;
  }
`;

const DuplicateToggle = styled.button`
  background: #CE0E2D;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #86091E;
  }
`;

const DuplicateTableWrapper = styled.div`
  overflow: auto;
  max-height: 150px;
  margin-top: 8px;
  display: inline-block;

  table {
    border-spacing: 0;
    border-collapse: collapse;
    border: 1px solid #ddd;

    th {
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: #eee;
      color: var(--clr-grey-2);
      font-size: 0.85em;
      font-weight: 600;
      padding: 4px 8px;
      text-align: center;
      border: 1px solid #e0e0e0;
    }

    td {
      padding: 3px 8px;
      font-size: 0.85em;
      border: 1px solid #e0e0e0;
      color: black;
      text-align: center;
    }

    tr {
      background-color: #f8f8f8;
      color: black;
    }

    tr:hover {
      background-color: #efefef;
    }
  }
`;

const ScrollHint = styled.div`
  font-size: 0.75em;
  color: var(--clr-grey-5);
  padding: 2px 0 0;
`;

const TopPanel = ({
  assignmentDate,
  duplicateBuses,
  handleDateChange,
  pulloutDivision,
  pulloutDivisions,
  rowFilter,
  rowFilterCodes,
  serviceType,
  setBoxJumpTo,
  setDuplicateBuses,
  setPulloutDivision,
  setRowFilter,
  setVehicleDivision,
  tableFetchFailed = false,
  updatedData,
  vehicleDivision,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [filterName, setFilterName] = useState(null);
  const [isDuplicatesExpanded, setIsDuplicatesExpanded] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const tableWrapperRef = useRef(null);

  useEffect(() => {
    if (isDuplicatesExpanded) {
      requestAnimationFrame(() => {
        const el = tableWrapperRef.current;
        if (el) {
          setIsScrollable(el.scrollHeight > el.clientHeight);
        }
      });
    } else {
      setIsScrollable(false);
    }
  }, [isDuplicatesExpanded, duplicateBuses]);

  const handleDuplicateClick = (value) => {
    setBoxJumpTo(value);
  };

  // eslint-disable-next-line react/prop-types
  const DuplicateTableBlockCell = ({ value }) => {
    return (
      <button
        type="button"
        style={{ cursor: "pointer", textDecoration: "underline", background: "none", border: "none", padding: 0, font: "inherit", color: "inherit" }}
        onClick={() => {
          handleDuplicateClick(value);
        }}
      >
        {value}
      </button>
    );
  };

  const handleChange = (type, value, additionalLogic = () => {}) => {
    if (updatedData.length !== 0) {
      setFilterName(type);
      setPendingData(value);
      setIsModalOpen(true);
    } else {
      additionalLogic(value);
    }
  };

  const applyPendingChange = {
    "Pullout Division": () => {
      setDuplicateBuses([]); // Reset duplicate buses
      setPulloutDivision(pendingData); // Apply the change
      setVehicleDivision(pendingData); // Update the vehicle division
    },
    "Row Filter": () => {
      setRowFilter(pendingData); // Apply the change
    },
    Date: () => {
      handleDateChange(pendingData); // Apply the change
    },
  };

  const handlePulloutDivisionChange = (e) => {
    handleChange("Pullout Division", e.target.value, (value) => {
      setDuplicateBuses([]); // Reset duplicate buses
      setPulloutDivision(value); // Apply the change
      setVehicleDivision(value); // Update the vehicle division
    });
  };

  const handleRowFilterChange = (e) => {
    handleChange("Row Filter", e.target.value, setRowFilter);
  };

  const setDateChange = (date) => {
    handleChange("Date", date, handleDateChange);
  };

  const confirmChange = () => {
    if (filterName && applyPendingChange[filterName]) {
      applyPendingChange[filterName]();
    }
    cancelChange();
  };

  const cancelChange = () => {
    setIsModalOpen(false); // Close the modal
    setPendingData(null); // Clear the pending state
    setFilterName(null); // Clear the filter name
  };

  const handleVehicleDivisionChange = (e) => {
    setVehicleDivision(e.target.value);
  };

  //Duplicate table
  const columns = useMemo(
    () => [
      {
        Header: "Bus",
        accessor: "lastVehicle",
        width: 50,
      },
      {
        Header: "Box",
        accessor: "boxNumber",
        width: 50,
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <DuplicateTableBlockCell value={value} />,
      },
      {
        Header: "Run Number",
        accessor: "runNumber",
        width: 100,
      },
      {
        Header: "Route",
        accessor: "primaryRouteAbbr",
        width: 80,
      },
      {
        Header: "Block",
        accessor: "blockNumber",
        disableFilters: true,
        width: 80,
      },
      {
        Header: "Time Out",
        accessor: "scheduledPulloutTimeFmt",
        disableFilters: true,
        width: 80,
      },
      {
        Header: "Time In",
        accessor: "scheduledPullinTimeFmt",
        disableFilters: true,
        width: 80,
      },
    ],
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      width: 250,
    }),
    []
  );

  const scrollBarSize = React.useMemo(() => scrollbarWidth(), []);
  const duplicateTableInstance = useTable(
    {
      columns,
      data: duplicateBuses,
      defaultColumn,
    },
    useFilters,
    useBlockLayout
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    totalColumnsWidth,
    prepareRow,
  } = duplicateTableInstance;

  return (
    <>
      <WarningMessage
        isModalOpen={isModalOpen}
        confirmChange={confirmChange}
        cancelChange={cancelChange}
        filterName={filterName}
      />
      <TopPanelContainer>
        <FormFieldsRow>
          <FormField>
            <label htmlFor="date">Date</label>
            <DatePicker
              selected={assignmentDate}
              onChange={setDateChange}
              className="datePicker"
              id="date"
              dateFormat="MM/dd/yyyy"
              disabled={tableFetchFailed}
            />
          </FormField>
          <FormField>
            <label htmlFor="servicetype">Service Type</label>
            <input
              type="text"
              value={serviceType}
              style={{ width: "140px" }}
              id="servicetype"
              readOnly
            />
          </FormField>
          <FormField>
            <label htmlFor="pulloutDivision">Pullout Division</label>
            <select
              name="pulloutDivision"
              id="pulloutDivision"
              style={{ width: "140px" }}
              onChange={handlePulloutDivisionChange}
              value={pulloutDivision}
            >
              {pulloutDivisions.map(({ divisionId, divisionName }) => (
                <option key={divisionId} value={divisionId}>
                  {divisionName}
                </option>
              ))}
            </select>
          </FormField>
          <FormField>
            <label htmlFor="RowFilter">Row Filter</label>
            <select
              name="RowFilter"
              id="RowFilter"
              style={{ width: "140px" }}
              onChange={handleRowFilterChange}
              value={rowFilter}
              disabled={tableFetchFailed}
            >
              {rowFilterCodes.map(({ code, description }) => (
                <option key={code} value={code}>
                  {description}
                </option>
              ))}
            </select>
          </FormField>
          <FormField>
            <label htmlFor="vehicleDivision">Vehicle Division</label>
            <select
              name="vehicleDivision"
              id="vehicleDivision"
              style={{ width: "140px" }}
              onChange={handleVehicleDivisionChange}
              value={vehicleDivision}
              disabled={tableFetchFailed}
            >
              {pulloutDivisions.map(({ divisionId, divisionName }) => (
                <option key={divisionId} value={divisionId}>
                  {divisionName}
                </option>
              ))}
            </select>
          </FormField>

          {duplicateBuses.length > 0 && (
            <DuplicateToggle
              type="button"
              onClick={() => setIsDuplicatesExpanded(!isDuplicatesExpanded)}
            >
              {duplicateBuses.length} Duplicate Bus{duplicateBuses.length !== 1 && "es"}{" "}
              {isDuplicatesExpanded ? "\u25B2" : "\u25BC"}
            </DuplicateToggle>
          )}
        </FormFieldsRow>

        {duplicateBuses.length > 0 && isDuplicatesExpanded && (
          <>
            <DuplicateTableWrapper ref={tableWrapperRef}>
              <table {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup, index) => {
                    const { key: _headerKey, ...headerGroupProps } =
                      headerGroup.getHeaderGroupProps();
                    return (
                      <tr key={_headerKey ?? index} {...headerGroupProps}>
                        {headerGroup.headers.map((column, colIndex) => {
                          const { key: _colKey, ...headerProps } =
                            column.getHeaderProps();
                          return (
                            <th
                              key={_colKey ?? colIndex}
                              width={column.width}
                              {...headerProps}
                            >
                              {column.render("Header")}
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>

                <tbody
                  {...getTableBodyProps({
                    style: {
                      width: totalColumnsWidth + scrollBarSize,
                    },
                  })}
                >
                  {rows.map((row, rowIndex) => {
                    prepareRow(row);
                    const { key: _rowKey, ...rowProps } =
                      row.getRowProps();
                    return (
                      <tr key={_rowKey ?? rowIndex} {...rowProps}>
                        {row.cells.map((cell, cellIndex) => {
                          const { key: _cellKey, ...cellProps } =
                            cell.getCellProps();
                          return (
                            <td key={_cellKey ?? cellIndex} {...cellProps}>
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DuplicateTableWrapper>
            {isScrollable && (
              <ScrollHint>↓ Scroll for more</ScrollHint>
            )}
          </>
        )}
      </TopPanelContainer>
    </>
  );
};

TopPanel.propTypes = {
  assignmentDate: PropTypes.instanceOf(Date).isRequired,
  duplicateBuses: PropTypes.array.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  pulloutDivision: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  pulloutDivisions: PropTypes.array.isRequired,
  rowFilter: PropTypes.string.isRequired,
  rowFilterCodes: PropTypes.array.isRequired,
  serviceType: PropTypes.string.isRequired,
  setBoxJumpTo: PropTypes.func.isRequired,
  setDuplicateBuses: PropTypes.func.isRequired,
  setPulloutDivision: PropTypes.func.isRequired,
  setRowFilter: PropTypes.func.isRequired,
  setVehicleDivision: PropTypes.func.isRequired,
  tableFetchFailed: PropTypes.bool,
  updatedData: PropTypes.array.isRequired,
  vehicleDivision: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default TopPanel;
